import { useDate } from "@website/accommodation-ui/hotel-search";
import {
  DatepickerSelectedDateType,
  getShowDatesInitial,
  INITIAL_SHOW_DATES_NUMBER
} from "@website/ui/datepicker";
import { InitialShowDatesType } from "@website/ui/datepicker-v2";
import { dateFormat } from "@website/utils/date-format";
import { deepOmit } from "@website/utils/deep-omit";
import { isFilled } from "@website/utils/is-filled";
import toString from "lodash-es/toString";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";

export const useCalendar = () => {
  const [calendarInitialShowDates, setCalendarInitialShowDates] = useState<
    InitialShowDatesType | undefined
  >(undefined);
  const router = useRouter();

  const { date_to, date_from } = router.query as Record<string, string>;

  const datePickerInitialValues: DatepickerSelectedDateType = useMemo(
    () => [
      {
        year: Number(
          dateFormat(date_from, {
            locale: "fa",
            numberConvert: false,
            template: "yyyy"
          })
        ),
        month: Number(
          dateFormat(date_from, {
            locale: "fa",
            numberConvert: false,
            template: "MM"
          })
        ),
        day: Number(
          dateFormat(date_from, {
            locale: "fa",
            numberConvert: false,
            template: "dd"
          })
        ),
        dateTime: toString(
          dateFormat(date_from, {
            locale: "en",
            template: "yyyy-MM-dd"
          })
        )
      },
      {
        year: Number(
          dateFormat(date_to, {
            locale: "fa",
            numberConvert: false,
            template: "yyyy"
          })
        ),
        month: Number(
          dateFormat(date_to, {
            locale: "fa",
            numberConvert: false,
            template: "MM"
          })
        ),
        day: Number(
          dateFormat(date_to, {
            locale: "fa",
            numberConvert: false,
            template: "dd"
          })
        ),
        dateTime: toString(
          dateFormat(date_to, {
            locale: "en",
            template: "yyyy-MM-dd"
          })
        )
      }
    ],
    [date_from, date_to]
  );

  const { date, onChangeDate } = useDate(datePickerInitialValues);

  const setQueryParameters = useCallback(
    (updateObject: Record<string, string | Array<string>>) =>
      router.replace(
        {
          pathname: router.pathname,
          query: deepOmit(
            {
              ...router.query,
              ...updateObject
            },
            isFilled
          )
        },
        undefined,
        { scroll: false, shallow: true }
      ),
    [router]
  );

  const onCalendarChange = useCallback(
    (date) => {
      onChangeDate(date);
      const dateFrom = date[0]?.dateTime;
      const dateTo = date[1]?.dateTime;
      if ((dateTo && dateFrom) || (!dateTo && !dateFrom)) {
        setQueryParameters({
          date_from: dateFrom,
          date_to: dateTo
        });
        // Synchronize the date-picker with the calendar
        const values = getShowDatesInitial(
          toString(dateFrom),
          INITIAL_SHOW_DATES_NUMBER,
          "persian"
        );
        setCalendarInitialShowDates(values);
      }
    },
    [onChangeDate, setCalendarInitialShowDates, setQueryParameters]
  );

  useEffect(() => {
    if ((date_to && date_from) || (!date_to && !date_from)) {
      const result =
        !date_to && !date_from
          ? []
          : [
              {
                year: Number(
                  dateFormat(date_from, {
                    locale: router.locale,
                    numberConvert: false,
                    template: "yyyy"
                  })
                ),
                month: Number(
                  dateFormat(date_from, {
                    locale: router.locale,
                    numberConvert: false,
                    template: "MM"
                  })
                ),
                day: Number(
                  dateFormat(date_from, {
                    locale: router.locale,
                    numberConvert: false,
                    template: "dd"
                  })
                ),
                dateTime: toString(
                  dateFormat(date_from, {
                    locale: "en",
                    template: "yyyy-MM-dd"
                  })
                )
              },
              {
                year: Number(
                  dateFormat(date_to, {
                    locale: router.locale,
                    numberConvert: false,
                    template: "yyyy"
                  })
                ),
                month: Number(
                  dateFormat(date_to, {
                    locale: router.locale,
                    numberConvert: false,
                    template: "MM"
                  })
                ),
                day: Number(
                  dateFormat(date_to, {
                    locale: router.locale,
                    numberConvert: false,
                    template: "dd"
                  })
                ),
                dateTime: toString(
                  dateFormat(date_to, {
                    locale: "en",
                    template: "yyyy-MM-dd"
                  })
                )
              }
            ];
      onChangeDate(result as DatepickerSelectedDateType);
    }
  }, [date_from, date_to]);

  return {
    calendarInitialShowDates,
    calendarDate: date,
    onCalendarChange,
    setQueryParameters
  };
};
